import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import classNames from 'classnames';
import { LoadingSpinner, TextField } from '../../index';
import { debounced1s, getCityState } from './helpers';
import CityStateHint from './CityStateHint';

const ZipcodeField = React.forwardRef<HTMLInputElement, ZipcodeFieldProps>(
  (
    {
      nameZipcode,
      labelZipcode,
      nameCity,
      labelCity,
      nameState,
      labelState,
      control,
      errorText,
      onInvalidZipcode,
      zipcodeApiKey,
    },
    ref
  ) => {
    const [lastValidatedZipcode, setLastValidatedZipcode] = useState('');
    const [isCityLoading, setIsCityLoading] = useState(false);
    const [isShowCityState, setIsShowCityState] = useState(false);

    const showCityStateFields = useCallback(() => {
      setIsShowCityState(true);
    }, []);

    const {
      field: { value: cityValueProp, onChange: cityOnChange, ref: cityRef },
    } = useController({
      name: nameCity,
      control,
    });

    const {
      field: { value: stateValueProp, onChange: stateOnChange, ref: stateRef },
    } = useController({
      name: nameState,
      control,
    });

    const validateZipcode = (value: string): Promise<string | boolean> => {
      if (lastValidatedZipcode === value) {
        return Promise.resolve(true);
      }

      setLastValidatedZipcode(value);

      cityOnChange(null);
      stateOnChange(null);
      setIsShowCityState(false);

      return new Promise<any>((resolve) => {
        if (value.length == 5 && /^[0-9]+$/.test(value)) {
          setIsCityLoading(true);

          debounced1s(async function loadCityState() {
            try {
              const { city, state } = await getCityState(zipcodeApiKey, value);

              cityOnChange(city);
              stateOnChange(state);

              setIsCityLoading(false);
              resolve(true);
            } catch (e) {
              setIsCityLoading(false);
              resolve(
                "No matches found. Please check zip code and make sure it's correct."
              );
              onInvalidZipcode && onInvalidZipcode(value);
            }
          });
        } else {
          resolve('Incorrect zipcode');
        }
      });
    };

    const rules = {
      validate: validateZipcode,
      required: true,
    };

    const {
      field: {
        value: zipcodeValueProp,
        onChange: zipcodeOnChange,
        ref: zipcodeRef,
      },
      meta: { invalid: invalidZipcode },
    } = useController({
      name: nameZipcode,
      rules,
      control,
    });

    const zipcodeHint = useMemo(() => {
      if (isCityLoading) {
        return <LoadingSpinner size="x-small" />;
      } else if (!isShowCityState && cityValueProp) {
        return (
          <CityStateHint
            city={cityValueProp}
            state={stateValueProp}
            clickHandler={showCityStateFields}
          />
        );
      }
      return null;
    }, [
      isCityLoading,
      isShowCityState,
      cityValueProp,
      stateValueProp,
      showCityStateFields,
    ]);

    const cityStateClassNames = useMemo(() => {
      return classNames({
        'ui-zipcode-city_state-hidden': !isShowCityState,
      });
    }, [isShowCityState]);

    return (
      <>
        <TextField
          label={labelZipcode}
          ref={zipcodeRef}
          name={nameZipcode}
          formHelperTextProps={{ as: 'div' }}
          maxLength={5}
          value={zipcodeValueProp || ''}
          helperText={zipcodeHint}
          onChange={zipcodeOnChange}
          errorText={errorText}
          hasError={!isCityLoading && invalidZipcode}
        />
        <TextField
          label={labelCity}
          formGroupClassName={cityStateClassNames}
          ref={cityRef}
          name={nameCity}
          value={cityValueProp || ''}
          onChange={cityOnChange}
        />
        <TextField
          label={labelState}
          formGroupClassName={cityStateClassNames}
          ref={stateRef}
          name={nameState}
          value={stateValueProp || ''}
          onChange={stateOnChange}
        />
      </>
    );
  }
);

export interface ZipcodeFieldProps {
  errorText?: string;
  control: Control;
  nameZipcode: string;
  labelZipcode?: string;
  nameCity: string;
  labelCity?: string;
  nameState: string;
  labelState?: string;
  onInvalidZipcode?: (value: string) => void;
  zipcodeApiKey: string;
}

export default ZipcodeField;
